import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import DropdownTreeSelect from "react-dropdown-tree-select";
import "react-dropdown-tree-select/dist/styles.css";

const StyledSelect = styled.div`
  &&& {
    position: relative;
    width: ${({ width }) => width || "170px"};
    margin: 0 13px 0 5px;
    .selected-summary {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 8px;
      right: 22px;
      display: flex;
      align-items: center;
      color: #666;
      font-size: 12px;
      pointer-events: none;
    }
    .selected-summary > span {
      width: 100%;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    .dropdown-content {
      min-width: 170px;
      max-height: ${({ maxHeight }) => maxHeight || "300px"};
      overflow-y: auto;
    }
    .dropdown,
    .dropdown-trigger {
      width: 100%;
    }
    .dropdown-trigger {
      background-color: white;
      border: none;
      height: 26.3px;
      overflow-y: hidden;
    }
    .tag-list {
      display: inline-block;
      width: 90%;
    }
    .react-dropdown-tree-select
      .dropdown
      .dropdown-trigger
      .tag-list
      .tag-item:not(:last-child) {
      display: none;
    }
    input[type="checkbox"] {
      opacity: 100;
      height: 10px;
      border: initial;
      display: inline-block;
    }
    .node-label {
      color: #333333;
    }
    .search {
      padding: 0px 0px;
    }
  }
  &&&.has-selection
    .react-dropdown-tree-select
    .dropdown
    .dropdown-trigger
    .tag-list
    .tag-item:last-child {
    visibility: hidden;
  }
`;

const getLeafValues = (node) => {
  if (!node.children || node.children.length === 0) {
    return [node.value];
  }
  return node.children.flatMap((child) => getLeafValues(child));
};

const findNodeByValue = (nodes, value) => {
  for (const node of nodes) {
    if (node.value === value) {
      return node;
    }
    if (node.children) {
      const foundNode = findNodeByValue(node.children, value);
      if (foundNode) return foundNode;
    }
  }
  return null;
};

// 선택된 리프 value("A|B|C|...") 목록을 { code, childrens } 중첩 트리로 복원한다.
const transResultData = (data) => {
  const addToTree = (tree, parts) => {
    if (parts.length === 0) return;

    const [current, ...rest] = parts; // 현재 노드와 나머지 분리
    let node = tree.find((entry) => entry.code === current);

    if (!node) {
      node = { code: current, childrens: [] }; // 새 노드 생성
      tree.push(node);
    }

    addToTree(node.childrens, rest);
  };

  const result = [];
  data.forEach((item) => {
    const parts = item.split("|");
    addToTree(result, parts);
  });

  return result;
};

// code, codeDesc
const CustomDropDownTreeSelect = ({
  data,
  placeholder,
  onChange,
  onFocus,
  isViewCode = false,
  flattenIntermediate = false,
  maxHeight,
  width,
}) => {
  const [transData, setTransData] = useState([]);
  const [selectData, setSelectData] = useState([]);
  const [focused, setFocused] = useState(false);

  // 메모이즈된 핸들러가 항상 최신 값을 참조하도록 ref 로 보관한다.
  // (핸들러 identity 를 고정해야 트리 엘리먼트를 재생성하지 않고 검색 상태가 유지된다.)
  const transDataRef = useRef([]);
  const onChangeRef = useRef(onChange);
  const onFocusRef = useRef(onFocus);
  useEffect(() => {
    onChangeRef.current = onChange;
    onFocusRef.current = onFocus;
  });

  useEffect(() => {
    const transformData = (data, parentCode = "") =>
      data.map((item) => {
        const newItem = {
          value: `${parentCode}${parentCode ? "|" : ""}${item.code}`,
          label: item.codeDesc,
          children: item.childrens
            ? transformData(
                item.childrens,
                `${parentCode}${parentCode ? "|" : ""}${item.code}`
              )
            : [],
        };

        if (newItem.children.length === 0 && isViewCode) {
          newItem.label = `${item.codeDesc} (${item.code})`;
        }

        return newItem;
      });

    // 최상위(예: Dealer/Port/Distributor) 노드는 유지하되, 중간 depth(Area/United/Country)를
    // 감추고 말단 리프(딜러)를 최상위 바로 아래로 끌어올린다. 리프의 value 는 전체 경로를
    // 그대로 유지하므로 서버로 전달되는 선택 데이터 구조는 변하지 않는다.
    const collectLeaves = (node) =>
      !node.children || node.children.length === 0
        ? [node]
        : node.children.flatMap(collectLeaves);

    const flattenTopLevel = (nodes) =>
      nodes.map((node) => ({
        ...node,
        children:
          node.children && node.children.length > 0
            ? node.children.flatMap(collectLeaves)
            : [],
      }));

    const transformed = transformData(data);
    const finalData = flattenIntermediate
      ? flattenTopLevel(transformed)
      : transformed;

    transDataRef.current = finalData;
    setTransData(finalData);
    // 옵션 데이터가 바뀌면 트리가 새로 그려지므로 선택 미러도 초기화한다.
    setSelectData([]);
  }, [data, isViewCode, flattenIntermediate]);

  useEffect(() => {
    onChangeRef.current(transResultData(selectData));
  }, [selectData]);

  // "placeholder (선택한 항목 수)" 형태의 요약 텍스트
  const summaryText = useMemo(() => {
    const count = selectData.length;
    if (count === 0) {
      return "";
    }
    return `${placeholder} (${count})`;
  }, [selectData, placeholder]);

  // 체크박스 변경만 selectData 미러에 반영한다. 체크/펼침 상태는 라이브러리가 내부적으로 관리하며,
  // 여기서 data 를 다시 만들어 넘기지 않으므로 진행 중이던 검색 필터가 초기화되지 않는다.
  const handleChange = useCallback((currentNode) => {
    const targetNode = findNodeByValue(transDataRef.current, currentNode.value);
    if (!targetNode) {
      return;
    }

    const leafValues =
      !targetNode.children || targetNode.children.length === 0
        ? [targetNode.value]
        : targetNode.children.flatMap((child) => getLeafValues(child));

    if (currentNode.checked) {
      setSelectData((prev) => [...new Set([...prev, ...leafValues])]);
    } else {
      setSelectData((prev) =>
        prev.filter((value) => !leafValues.includes(value))
      );
    }
  }, []);

  const handleFocus = useCallback(() => {
    setFocused(true);
    if (onFocusRef.current) onFocusRef.current();
  }, []);

  const handleBlur = useCallback(() => setFocused(false), []);

  // 트리 엘리먼트는 transData(옵션 구성)가 바뀔 때만 재생성된다. selectData/focused 변경 시에는
  // 동일 엘리먼트 참조가 유지되어 react-dropdown-tree-select 가 재초기화되지 않는다.
  const treeElement = useMemo(
    () => (
      <DropdownTreeSelect
        mode="multiSelect"
        data={transData}
        showDropdown="default"
        texts={{ placeholder }}
        showPartiallySelected={true}
        inlineSearchInput={true}
        keepTreeOnSearch={true}
        onChange={handleChange}
        closeOnSelect={false}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    ),
    [transData, placeholder, handleChange, handleFocus, handleBlur]
  );

  const hasSelection = !focused && !!summaryText;

  return (
    <StyledSelect
      maxHeight={maxHeight}
      width={width}
      className={hasSelection ? "has-selection" : undefined}
    >
      {treeElement}
      {hasSelection && (
        <div className="selected-summary">
          <span>{summaryText}</span>
        </div>
      )}
    </StyledSelect>
  );
};

export default CustomDropDownTreeSelect;
